import { useCallback } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { userPreferenceAPI } from '@atlassian/jira-user-preferences-services/src/utils/index.tsx';
import { createResource, useResource } from '@atlassian/react-resource-router';
import { STORE_KEY, RESOURCE_TYPE, RESOURCE_KEY, PACKAGE_NAME } from './constants.tsx';

import type {
	GICSmartFieldsFeatureResourceData,
	GICSmartFieldsMyPreferencesResponse,
} from './types.tsx';

export const gicSmartFieldsFeatureResource = createResource<GICSmartFieldsFeatureResourceData>({
	type: RESOURCE_TYPE,
	getKey: () => RESOURCE_KEY,
	getData: async (): Promise<GICSmartFieldsFeatureResourceData> => {
		try {
			const data = await userPreferenceAPI.get<GICSmartFieldsMyPreferencesResponse>(STORE_KEY);
			if (!data)
				return {
					hasUserOptedOut: false,
					hasUserDismissedReadMore: false,
					hasUserDismissedGicOnboarding: false,
				};
			return data;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			// If value has never been set, we expect a 404
			if (error.statusCode === 404) {
				return {
					hasUserOptedOut: false,
					hasUserDismissedReadMore: false,
					hasUserDismissedGicOnboarding: false,
				};
			}
			fireErrorAnalytics({
				error,
				meta: {
					id: 'gicSmartFieldsFeatureResource',
					packageName: PACKAGE_NAME,
					teamName: 'gryffindor',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
		}
		return {
			hasUserOptedOut: false,
			hasUserDismissedReadMore: false,
			hasUserDismissedGicOnboarding: false,
		};
	},
	maxAge: Number.MAX_VALUE,
});

export const useIsSmartFieldsOptIn = () => {
	const { data, update, loading } = useResource(gicSmartFieldsFeatureResource);

	const currentOptInStatus =
		typeof data?.hasUserOptedOut === 'boolean' ? !data.hasUserOptedOut : true;

	const currentDismissedReadMore =
		typeof data?.hasUserDismissedReadMore === 'boolean' ? data.hasUserDismissedReadMore : false;

	const currentDismissedGicOnboarding =
		typeof data?.hasUserDismissedGicOnboarding === 'boolean'
			? data.hasUserDismissedGicOnboarding
			: false;

	const dismissReadMore = useCallback(() => {
		update((previousData) => ({
			...(previousData?.hasUserOptedOut
				? { hasUserOptedOut: previousData.hasUserOptedOut }
				: { hasUserOptedOut: false }),
			hasUserDismissedReadMore: true,
			...(previousData?.hasUserDismissedGicOnboarding
				? { hasUserDismissedGicOnboarding: previousData.hasUserDismissedGicOnboarding }
				: { hasUserDismissedGicOnboarding: false }),
		}));
	}, [update]);

	const dismissGicOnboarding = useCallback(() => {
		update((previousData) => ({
			...(previousData?.hasUserOptedOut
				? { hasUserOptedOut: previousData.hasUserOptedOut }
				: { hasUserOptedOut: false }),
			...(previousData?.hasUserDismissedReadMore
				? { hasUserDismissedReadMore: previousData.hasUserDismissedReadMore }
				: { hasUserDismissedReadMore: false }),
			hasUserDismissedGicOnboarding: true,
		}));
	}, [update]);

	return {
		isSmartFieldsOptIn: loading ? null : currentOptInStatus,
		isSmartFieldDismissedReadMore: loading ? null : currentDismissedReadMore,
		isSmartFieldsGicOnboardingDismissed: loading ? null : currentDismissedGicOnboarding,
		dismissReadMore,
		dismissGicOnboarding,
		loading,
	};
};
